import { useState } from 'react';
import { formatHours } from '../../utils/flightDisplay';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import ModalPortal from '../modal/ModalPortal';
import './SplitFlightModal.css';

interface Props {
  flightId: number;
  flightHours: number;
  pilots: { id: number; name: string }[];
  onClose: () => void;
  onSplit: (shares: { pilot_id: number; hours: number }[]) => void | Promise<void>;
}

const SplitFlightModal = ({ flightId, flightHours, pilots, onClose, onSplit }: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const [shares, setShares] = useState<{ pilot_id: number; hours: string }[]>(
    pilots.map((p) => ({ pilot_id: p.id, hours: '' }))
  );

  const totalShares = shares.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0);

  const handleSubmit = () => {
    if (submitting) return;
    const formattedShares = shares
      .filter((s) => s.hours && parseFloat(s.hours) > 0)
      .map((s) => ({ pilot_id: s.pilot_id, hours: parseFloat(s.hours) }));

    const total = formattedShares.reduce((sum, s) => sum + s.hours, 0);
    if (Math.abs(total - flightHours) > 0.01) {
      showError(
        `Сумма долей (${formatHours(total)}) не равна налёту полёта (${formatHours(flightHours)} м/ч)`
      );
      return;
    }
    void runSubmit(() => onSplit(formattedShares));
  };

  return (
    <ModalPortal>
    <div className="modal-overlay">
      {feedbackNode}
      <div className="modal split-flight-modal">
        <h3>Разделить полёт #{flightId}</h3>
        <p>Всего: {formatHours(flightHours)} м/ч</p>
        <div className="shares-list">
          {pilots.map((p, idx) => (
            <div key={p.id} className="share-row">
              <span>{p.name}</span>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="м/ч"
                value={shares[idx].hours}
                onChange={(e) => {
                  const newShares = [...shares];
                  newShares[idx].hours = e.target.value;
                  setShares(newShares);
                }}
              />
            </div>
          ))}
        </div>
        <p>Распределено: {formatHours(totalShares)} / {formatHours(flightHours)} м/ч</p>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? '…' : 'Разделить'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default SplitFlightModal;